import { Box, Flex } from '@radix-ui/themes';
import { ApexOptions } from 'apexcharts';
import React, { useState } from 'react';
import Chart from 'react-apexcharts';
import { Lists } from '../../../components/common/Lists';
import TableComponent from '../../../components/common/Tables';
import { useGetActiveDebtsQuery, useGetComplianceRateQuery, useGetDebtDistributionByPaymentTypeQuery, useGetDebtsByPeriodQuery, useGetOverdueDebtsQuery, useGetPaymentsHistoryByStudentQuery, useGetStudentsWithMostDelaysQuery, useGetTotalPaymentsByPeriodsQuery, useGetTotalsLatesFeesQuery } from '../../../hooks/useFinanceReportsQuery';
import DateFieldComponent from '../../../components/inputs/Datefield/DatefieldComponent';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

const columnNamesMapGetActiveDebt: { [key: string]: string } = {
  studentName: 'Nome do Estudante',
  paymentType: 'Tipo de pagamento',
  originalAmount: 'Valor original',
  currentAmount: 'Valor atual',
  dueDate: 'Data de vencimento',
  daysOverdue: 'Dias de atraso',
};

const columnNamesMapGetDebtsByPeriod: { [key: string]: string } = {
  paymentType: 'Tipo de pagamento',
  totalOriginalAmount: 'Valor original total',
  totalCurrentAmount: 'Valor atual total',
  debtCount: 'Contagem de Cobranças',
  paidDebts: 'Cobranças pagas',
  unpaidDebts: 'Cobranças não pagas',
};

const columnNamesMapGetOverdueDebts: { [key: string]: string } = {
  studentName: 'Nome do Estudante',
  paymentType: 'Tipo de pagamento',
  originalAmount: 'Valor original',
  currentAmount: 'Valor atual',
  dueDate: 'Data de vencimento',
  daysOverdue: 'Dias de atraso',
};

const columnNamesMapGetDebtDistributionByPaymentType: { [key: string]: string } = {
  paymentType: 'Tipo de pagamento',
  totalDebts: 'Cobranças totais',
  totalDebtAmount: 'Valor total da Cobrança',
  paidDebts: 'Cobranças pagas',
  unpaidDebts: 'Cobranças não pagas',
};

const columnNamesMapGetPaymentHistoryByStudent: { [key: string]: string } = {
  date: 'Data',
  paymentMethod: 'Método de pagamento',
  paymentType: 'Tipo de pagamento',
  amountPaid: 'Valor pago',
  transactionReference: 'Referência de transação',
  status: 'Estado',
};

const columnNamesMapGetTotalPaymentsByPeriod: { [key: string]: string } = {
  month: 'Mês',
  totalAmountPaid: 'Valor total pago',
  transactionCount: 'Contagem de transações',
};

const columnNamesMapGetComplianceRate: { [key: string]: string } = {
  totalDebts: 'Cobranças totais',
  paidDebts: 'Cobranças pagas',
  unpaidDebts: 'Cobranças não pagas',
  complianceRate: 'Taxa de conformidade',
};

const columnNamesMapGetTotalLateFees: { [key: string]: string } = {
  paymentType: 'Tipo de pagamento',
  totalLateFees: 'Total de taxas em atraso',
  lateFeeCount: 'Contagem de taxas atrasadas',
};

const columnNamesMapGetStudentsWithMostDelays: { [key: string]: string } = {
  fullname: 'Nome completo',
  totalDelayedDebts: 'Total de Cobranças em atraso',
  totalDelayedAmount: 'Valor Total Atrasado',
  averageDaysOverdue: 'Média de dias de atraso',
};

const FinanceReports: React.FC = () => {

  const [currentPage, setCurrentPage] = useState(1);
  const { data: getActiveDebtsData, status: getActiveDebtsStatus } = useGetActiveDebtsQuery({ PageNumber: currentPage, PageSize: 400 });
  const { data: getOverdueDebtsData, status: getOverdueDebtsStatus } = useGetOverdueDebtsQuery({ PageNumber: currentPage, PageSize: 400 });
  const { data: getDebtDistributionByPaymentTypeData, status: getDebtDistributionByPaymentTypeStatus } = useGetDebtDistributionByPaymentTypeQuery({ PageNumber: currentPage, PageSize: 400 });
  const { data: getPaymentsHistoryByStudentData, status: getPaymentsHistoryByStudentStatus } = useGetPaymentsHistoryByStudentQuery({ PageNumber: currentPage, PageSize: 400 });
  const { data: getTotalPaymentsByPeriodsData, status: getTotalPaymentsByPeriodsStatus } = useGetTotalPaymentsByPeriodsQuery({ PageNumber: currentPage, PageSize: 400 });
  const { data: getComplianceRateData, status: getComplianceRateStatus } = useGetComplianceRateQuery({ PageNumber: currentPage, PageSize: 400 });
  const { data: getTotalsLatesFeesData, status: getTotalsLatesFeesStatus } = useGetTotalsLatesFeesQuery({ PageNumber: currentPage, PageSize: 400 });
  const { data: getStudentsWithMostDelaysData, status: getStudentsWithMostDelaysStatus } = useGetStudentsWithMostDelaysQuery({ PageNumber: currentPage, PageSize: 400 });

  
  type FormGetdebtsbyperiodType = {
    startDate: string, 
    endDate: string
  }

  const isValidDate = (value: string) => {
    const date = new Date(value);
    return !isNaN(date.getTime());
  };
  
  const isAfterStartDate = (startDate: string, endDate: string) => {
    if (!startDate || !endDate) return true;
    return new Date(startDate) <= new Date(endDate); // Corrigido para <=
  };
  
  const isBeforeEndDate = (startDate: string, endDate: string) => {
    if (!startDate || !endDate) return true;
    return new Date(startDate) <= new Date(endDate); // Corrigido para <=
  };
  
  const FormValidationGetdebtsbyperiod = yup.object().shape({
    startDate: yup
      .string()
      .required("Data obrigatória")
      .test("is-date", "Data inválida", isValidDate)
      .test("is-before-end", "O início deve ser antes ou igual ao fim.", function (value) {
        return isBeforeEndDate(value, this.parent.endDate);
      }),
  
    endDate: yup
      .string()
      .required("Data obrigatória")
      .test("is-date", "Data inválida", isValidDate)
      .test("is-after-start-date", "O fim deve ser depois ou igual ao início.", function (value) {
        return isAfterStartDate(this.parent.startDate, value);
      }),
  });
  
  const { control:controlGetdebtsbyperiod, handleSubmit:handleSubmitGetdebtsbyperiod, setValue:setValueGetdebtsbyperiod, getValues:getValuesGetdebtsbyperiod, formState: { errors:errorsGetdebtsbyperiod }, watch: watchGetdebtsbyperiod,trigger:triggerGetdebtsbyperiod } = useForm<FormGetdebtsbyperiodType>({
    resolver: yupResolver(FormValidationGetdebtsbyperiod),
    defaultValues:{
      endDate:'',
      startDate:'',
    },

    mode: 'onBlur',
    reValidateMode: 'onBlur',
    shouldUseNativeValidation: false,
});

const handleOnChangeStartDate = (startDateField:any) =>{
  const startDate =startDateField.target.value;
  setValueGetdebtsbyperiod('startDate', startDate)
  triggerGetdebtsbyperiod()
  refetchGetDebtsByPeriod()
}
const handleOnChangeEndDate = (endDateField:any) =>{
  const endDate= endDateField.target.value;
  setValueGetdebtsbyperiod('endDate', endDate)
  triggerGetdebtsbyperiod()
  refetchGetDebtsByPeriod()
}
const { data: getDebtsByPeriodData, status: getDebtsByPeriodStatus, refetch: refetchGetDebtsByPeriod } = useGetDebtsByPeriodQuery({ PageNumber: currentPage, PageSize: 400, StartDate: getValuesGetdebtsbyperiod('startDate'), EndDate: getValuesGetdebtsbyperiod('endDate') });

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const getActiveDebtsChartOptions: ApexOptions = {
    chart: {
      type: 'bar',
      height: 350
    },
    title: {
      text: 'Cobranças Ativas por Estudante e Tipo de Pagamento',
      align: 'center',
    },
    xaxis: {
      categories: getActiveDebtsData?.items.map(activeDebts => `${activeDebts.studentName} - ${activeDebts.paymentType}`) || [],
      title: {
        text: 'Estudante e Tipo de Pagamento',
      },
    },
    yaxis: {
      title: {
        text: 'Valores (MT)',
      },
    },
    series: [
      {
        name: 'Valor original',
        data: getActiveDebtsData?.items.map(activeDebts => activeDebts.originalAmount || 0) || []
      },
      {
        name: 'Valor atual',
        data: getActiveDebtsData?.items.map(activeDebts => activeDebts.currentAmount) || []
      }
    ]
  };

  const getDebtsByPeriodChartOptions: ApexOptions = {
    chart: {
      type: 'bar',
      height: 350
    },
    title: {
      text: 'Cobranças por Período',
      align: 'center',
    },
    xaxis: {
      categories: getDebtsByPeriodData?.items.map(debtsByPeriod => `${debtsByPeriod.paymentType}`) || [],
      title: {
        text: 'Tipo de Pagamento',
      },
    },
    yaxis: {
      title: {
        text: 'Valores (MT)',
      },
    },
    series: [
      {
        name: 'Valor original total',
        data: getDebtsByPeriodData?.items.map(debtsByPeriod => debtsByPeriod.totalOriginalAmount || 0) || []
      },
      {
        name: 'Valor atual total',
        data: getDebtsByPeriodData?.items.map(debtsByPeriod => debtsByPeriod.totalCurrentAmount) || []
      }
    ]
  };

  const getOverdueDebtsChartOptions: ApexOptions = {
    chart: {
      type: 'bar',
      height: 350
    },
    title: {
      text: 'Cobranças em Atraso por Estudante e Tipo de Pagamento',
      align: 'center',
    },
    xaxis: {
      categories: getOverdueDebtsData?.items.map(overdueDebts => `${overdueDebts.studentName || 'Estudante desconhecido'} - ${overdueDebts.paymentType}`) || [],
      title: {
        text: 'Estudante e Tipo de Pagamento',
      },
    },
    yaxis: {
      title: {
        text: 'Valores (MT)',
      },
    },
    series: [
      {
        name: 'Valor original',
        data: getOverdueDebtsData?.items.map(overdueDebts => overdueDebts.originalAmount || 0) || []
      },
      {
        name: 'Valor atual',
        data: getOverdueDebtsData?.items.map(overdueDebts => overdueDebts.currentAmount) || []
      }
    ]
  };

  const getDebtDistributionByPaymentTypeChartOptions: ApexOptions = {
    chart: {
      type: 'bar',
      height: 350
    },
    title: {
      text: 'Distribuição de Cobranças por Tipo de Pagamento',
      align: 'center',
    },
    xaxis: {
      categories: getDebtDistributionByPaymentTypeData?.items.map(debtDistributionByPaymentType => `${debtDistributionByPaymentType.paymentType}`) || [],
      title: {
        text: 'Tipo de Pagamento',
      },
    },
    yaxis: {
      title: {
        text: 'Quantidade de Cobranças',
      },
    },
    series: [
      {
        name: 'Cobranças totais',
        data: getDebtDistributionByPaymentTypeData?.items.map(debtDistributionByPaymentType => debtDistributionByPaymentType.totalDebts) || []
      },
      {
        name: 'Cobranças pagas',
        data: getDebtDistributionByPaymentTypeData?.items.map(debtDistributionByPaymentType => debtDistributionByPaymentType.paidDebts) || []
      },
      {
        name: 'Cobranças não pagas',
        data: getDebtDistributionByPaymentTypeData?.items.map(debtDistributionByPaymentType => debtDistributionByPaymentType.unpaidDebts) || []
      }
    ]
  };

  const getDebtDistributionPieChartOptions: ApexOptions = {
    chart: {
      type: 'pie',
      height: 350
    },
    title: {
      text: 'Distribuição de Cobranças por Tipo de Pagamento',
      align: 'center',
    },
    labels: getDebtDistributionByPaymentTypeData?.items.map(debt => debt.paymentType) || [],
    series: getDebtDistributionByPaymentTypeData?.items.map(debt => debt.totalDebtAmount) || [],
    responsive: [{
      breakpoint: 480,
      options: {
        chart: {
          width: 200
        },
        legend: {
          position: 'bottom'
        }
      }
    }]
  };

  const getPaymentsHistoryByStudentChartOptions: ApexOptions = {
    chart: {
      type: 'bar',
      height: 350
    },
    title: {
      text: 'Histórico de Pagamentos por Estudante',
      align: 'center',
    },
    xaxis: {
      categories: getPaymentsHistoryByStudentData?.items.map(paymentsHistoryByStudent => `${paymentsHistoryByStudent.date.toLocaleDateString('pt-PT')}`) || [],
      title: {
        text: 'Data do Pagamento',
      },
    },
    yaxis: {
      title: {
        text: 'Valor Pago (MT)',
      },
    },
    series: [
      {
        name: 'Valor pago',
        data: getPaymentsHistoryByStudentData?.items.map(paymentsHistoryByStudent => paymentsHistoryByStudent.amountPaid) || []
      },
    ],
  };

  const getPaymentsHistoryLineChartOptions: ApexOptions = {
    chart: {
      type: 'line',
      height: 350
    },
    title: {
      text: 'Histórico de Pagamentos por Estudante',
      align: 'center',
    },
    xaxis: {
      categories: getPaymentsHistoryByStudentData?.items.map(payments => payments.date.toLocaleDateString('pt-PT')) || [],
      title: {
        text: 'Data do Pagamento',
      },
    },
    yaxis: {
      title: {
        text: 'Valor Pago (MT)',
      },
    },
    series: [
      {
        name: 'Valor pago',
        data: getPaymentsHistoryByStudentData?.items.map(payments => payments.amountPaid) || []
      },
    ],
  };


  const getTotalPaymentsByPeriodsChartOptions: ApexOptions = {
    chart: {
      type: 'bar',
      height: 350
    },
    title: {
      text: 'Total de Pagamentos por Período',
      align: 'center',
    },
    xaxis: {
      categories: getTotalPaymentsByPeriodsData?.items.map(totalPaymentsByPeriods => `Mês ${totalPaymentsByPeriods.month}`) || [],
      title: {
        text: 'Meses',
      },
    },
    yaxis: {
      title: {
        text: 'Valores (MT)',
      },
    },
    series: [
      {
        name: 'Valor total pago',
        data: getTotalPaymentsByPeriodsData?.items.map(totalPaymentsByPeriods => totalPaymentsByPeriods.totalAmountPaid) || []
      },
      {
        name: 'Contagem de transações',
        data: getTotalPaymentsByPeriodsData?.items.map(totalPaymentsByPeriods => totalPaymentsByPeriods.transactionCount) || []
      }
    ]
  };

  const getTotalPaymentsAreaChartOptions: ApexOptions = {
    chart: {
      type: 'area',
      height: 350
    },
    title: {
      text: 'Total de Pagamentos por Período',
      align: 'center',
    },
    xaxis: {
      categories: getTotalPaymentsByPeriodsData?.items.map(period => `Mês ${period.month}`) || [],
      title: {
        text: 'Meses',
      },
    },
    yaxis: {
      title: {
        text: 'Valores (MT)',
      },
    },
    series: [
      {
        name: 'Valor total pago',
        data: getTotalPaymentsByPeriodsData?.items.map(period => period.totalAmountPaid) || []
      },
    ],
  };

  const getComplianceRateChartOptions: ApexOptions = {
    chart: {
      type: 'bar',
      height: 350
    },
    title: {
      text: 'Taxa de Conformidade',
      align: 'center',
    },
    xaxis: {
      categories: ['Cobranças totais', 'Cobranças pagas', 'Cobranças não pagas', 'Taxa de conformidade (%)'],
      title: {
        text: 'Categorias',
      },
    },
    yaxis: {
      title: {
        text: 'Quantidade',
      },
    },
    series: [
      {
        name: 'Quantidade',
        data: [
          getComplianceRateData?.totalDebts || 0,
          getComplianceRateData?.paidDebts || 0,
          getComplianceRateData?.unpaidDebts || 0,
          getComplianceRateData?.complianceRate || 0
        ]
      }
    ]
  };

  const getTotalsLatesFeesChartOptions: ApexOptions = {
    chart: {
      type: 'bar',
      height: 350
    },
    title: {
      text: 'Taxas em Atraso',
      align: 'center',
    },
    xaxis: {
      categories: getTotalsLatesFeesData?.items.map(totalsLatesFees => `${totalsLatesFees.paymentType}`) || [],
      title: {
        text: 'Categorias',
      },
    },
    yaxis: {
      title: {
        text: 'Valores (MT)',
      },
    },
    series: [
      {
        name: 'Total de taxas em atraso',
        data: getTotalsLatesFeesData?.items.map(totalsLatesFees => totalsLatesFees.totalLateFees || 0) || []
      },
      {
        name: 'Contagem de taxas atrasadas',
        data: getTotalsLatesFeesData?.items.map(totalsLatesFees => totalsLatesFees.lateFeeCount) || []
      }
    ]
  };

  const getStudentsWithMostDelaysChartOptions: ApexOptions = {
    chart: {
      type: 'bar',
      height: 350
    },
    title: {
      text: 'Estudantes com Mais Atrasos',
      align: 'center',
    },
    xaxis: {
      categories: getStudentsWithMostDelaysData?.items.map(studentsWithMostDelays => `${studentsWithMostDelays.fullname}`) || [],
      title: {
        text: 'Nome do Estudante',
      },
    },
    yaxis: {
      title: {
        text: 'Valores',
      },
    },
    series: [
      {
        name: 'Total de Cobranças em atraso',
        data: getStudentsWithMostDelaysData?.items.map(studentsWithMostDelays => studentsWithMostDelays.totalDelayedDebts) || []
      },
      {
        name: 'Valor Total Atrasado',
        data: getStudentsWithMostDelaysData?.items.map(studentsWithMostDelays => studentsWithMostDelays.totalDelayedAmount) || []
      },
      {
        name: 'Média de dias de atraso',
        data: getStudentsWithMostDelaysData?.items.map(studentsWithMostDelays => studentsWithMostDelays.averageDaysOverdue) || []
      }
    ]
  };
  return (
    <Box>

      <h1>Relatório de Pagamentos</h1>

      <h2>Cobranças Ativas por Estudante e Tipo de Pagamento</h2>
      <Lists
        status={getActiveDebtsStatus}
        TableComponent={() => <TableComponent
          columns={Object.keys(columnNamesMapGetActiveDebt)}
          items={getActiveDebtsData?.items}
          options={[]}
          columnNamesMap={columnNamesMapGetActiveDebt}
          totalPages={getActiveDebtsData?.totalPages || 1}
          currentPage={currentPage}
          onPageChange={handlePageChange}
        />}
      />

      {getActiveDebtsData?.items && getActiveDebtsData?.items.length > 0 && <Chart options={getActiveDebtsChartOptions} series={getActiveDebtsChartOptions.series} type="bar" height={350} />}

      <h2>Cobranças por Período</h2>
     
      <Lists
        status={getDebtsByPeriodStatus}
        TableComponent={() => <TableComponent
          columns={Object.keys(columnNamesMapGetDebtsByPeriod)}
          items={getDebtsByPeriodData?.items}
          options={[]}
          columnNamesMap={columnNamesMapGetDebtsByPeriod}
          totalPages={getDebtsByPeriodData?.totalPages || 1}
          currentPage={currentPage}
          onPageChange={handlePageChange}
          filterFields={()=>
            <>
            {/* <form onSubmit={onSubmitGetdebtsbyperiod}  noValidate> */}
            <DateFieldComponent 
                               name="startDate"
                               label="Data de início"
                               control={controlGetdebtsbyperiod}
                               errors={errorsGetdebtsbyperiod}
                               fieldProps={{onChange:((e:any)=> {
                                  handleOnChangeStartDate(e);
                               })}}
                              />
            <DateFieldComponent
                               name="endDate"
                               label="Data de fim"
                               control={controlGetdebtsbyperiod}
                               errors={errorsGetdebtsbyperiod}
                               fieldProps={{onChange:handleOnChangeEndDate}}
                               />
            {/* </form> */}
            </>
          }
        />}
      />
      

      {getDebtsByPeriodData?.items && getDebtsByPeriodData?.items.length > 0 && <Chart options={getDebtsByPeriodChartOptions} series={getDebtsByPeriodChartOptions.series} type="bar" height={350} />}

      <h2>Cobranças em Atraso por Estudante e Tipo de Pagamento</h2>
      <Lists
        status={getOverdueDebtsStatus}
        TableComponent={() => <TableComponent
          columns={Object.keys(columnNamesMapGetOverdueDebts)}
          items={getOverdueDebtsData?.items}
          options={[]}
          columnNamesMap={columnNamesMapGetOverdueDebts}
          totalPages={getOverdueDebtsData?.totalPages || 1}
          currentPage={currentPage}
          onPageChange={handlePageChange}
        />}
      />

      {getOverdueDebtsData?.items && getOverdueDebtsData?.items.length > 0 && <Chart options={getOverdueDebtsChartOptions} series={getOverdueDebtsChartOptions.series} type="bar" height={350} />}

      <h2>Distribuição de Cobranças por Tipo de Pagamento</h2>
      <Lists
        status={getDebtDistributionByPaymentTypeStatus}
        TableComponent={() => <TableComponent
          columns={Object.keys(columnNamesMapGetDebtDistributionByPaymentType)}
          items={getDebtDistributionByPaymentTypeData?.items}
          options={[]}
          columnNamesMap={columnNamesMapGetDebtDistributionByPaymentType}
          totalPages={getDebtDistributionByPaymentTypeData?.totalPages || 1}
          currentPage={currentPage}
          onPageChange={handlePageChange}
        />}
      />

      {getDebtDistributionByPaymentTypeData?.items && getDebtDistributionByPaymentTypeData?.items.length > 0 &&
        <>
          <Chart options={getDebtDistributionByPaymentTypeChartOptions} series={getDebtDistributionByPaymentTypeChartOptions.series} type="bar" height={350} />
          <Chart options={getDebtDistributionPieChartOptions} series={getDebtDistributionPieChartOptions.series} type="pie" height={350} />
        </>
      }

      <h2>Histórico de Pagamentos por Estudante</h2>
      <Lists
        status={getPaymentsHistoryByStudentStatus}
        TableComponent={() => <TableComponent
          columns={Object.keys(columnNamesMapGetPaymentHistoryByStudent)}
          items={getPaymentsHistoryByStudentData?.items}
          options={[]}
          columnNamesMap={columnNamesMapGetPaymentHistoryByStudent}
          totalPages={getPaymentsHistoryByStudentData?.totalPages || 1}
          currentPage={currentPage}
          onPageChange={handlePageChange}
        />}
      />

      {getPaymentsHistoryByStudentData?.items && getPaymentsHistoryByStudentData?.items.length > 0 &&
        <>
          <Chart options={getPaymentsHistoryByStudentChartOptions} series={getPaymentsHistoryByStudentChartOptions.series} type="bar" height={350} />
          <Chart options={getPaymentsHistoryLineChartOptions} series={getPaymentsHistoryLineChartOptions.series} type="line" height={350} />
        </>
      }

      <h2>Total de Pagamentos por Período</h2>
      <Lists
        status={getTotalPaymentsByPeriodsStatus}
        TableComponent={() => <TableComponent
          columns={Object.keys(columnNamesMapGetTotalPaymentsByPeriod)}
          items={getTotalPaymentsByPeriodsData?.items}
          options={[]}
          columnNamesMap={columnNamesMapGetTotalPaymentsByPeriod}
          totalPages={getTotalPaymentsByPeriodsData?.totalPages || 1}
          currentPage={currentPage}
          onPageChange={handlePageChange}
        />}
      />

      {getTotalPaymentsByPeriodsData?.items && getTotalPaymentsByPeriodsData?.items.length > 0 &&
        <>
          <Chart options={getTotalPaymentsByPeriodsChartOptions} series={getTotalPaymentsByPeriodsChartOptions.series} type="bar" height={350} />
          <Chart options={getTotalPaymentsAreaChartOptions} series={getTotalPaymentsAreaChartOptions.series} type="area" height={350} />
        </>
      }

      <h2>Taxa de Conformidade</h2>
      <Lists
        status={getComplianceRateStatus}
        TableComponent={() => <TableComponent
          columns={Object.keys(columnNamesMapGetComplianceRate)}
          items={[getComplianceRateData || {}]}
          options={[]}
          columnNamesMap={columnNamesMapGetComplianceRate}
          totalPages={1}
          currentPage={currentPage}
          onPageChange={handlePageChange}
        />}
      />

      {getComplianceRateData && <Chart options={getComplianceRateChartOptions} series={getComplianceRateChartOptions.series} type="bar" height={350} />}

      <h2>Taxas em Atraso</h2>
      <Lists
        status={getTotalsLatesFeesStatus}
        TableComponent={() => <TableComponent
          columns={Object.keys(columnNamesMapGetTotalLateFees)}
          items={getTotalsLatesFeesData?.items}
          options={[]}
          columnNamesMap={columnNamesMapGetTotalLateFees}
          totalPages={getTotalsLatesFeesData?.totalPages || 1}
          currentPage={currentPage}
          onPageChange={handlePageChange}
        />}
      />

      {getTotalsLatesFeesData?.items && getTotalsLatesFeesData?.items.length > 0 && <Chart options={getTotalsLatesFeesChartOptions} series={getTotalsLatesFeesChartOptions.series} type="bar" height={350} />}

      <h2>Estudantes com Mais Atrasos</h2>
      <Lists
        status={getStudentsWithMostDelaysStatus}
        TableComponent={() => <TableComponent
          columns={Object.keys(columnNamesMapGetStudentsWithMostDelays)}
          items={getStudentsWithMostDelaysData?.items}
          options={[]}
          columnNamesMap={columnNamesMapGetStudentsWithMostDelays}
          totalPages={getStudentsWithMostDelaysData?.totalPages || 1}
          currentPage={currentPage}
          onPageChange={handlePageChange}
        />}
      />

      {getStudentsWithMostDelaysData?.items && getStudentsWithMostDelaysData?.items.length > 0 && <Chart options={getStudentsWithMostDelaysChartOptions} series={getStudentsWithMostDelaysChartOptions.series} type="bar" height={350} />}

    </Box>
  );
};
export default FinanceReports;
